import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroAdjustmentsHorizontal,
  heroBolt,
  heroChatBubbleLeftRight,
  heroCheck,
  heroChevronDown,
  heroCommandLine,
  heroSparkles,
} from '@ng-icons/heroicons/outline';
import { NgpDialogTrigger } from 'ng-primitives/dialog';
import { NgpMenuTrigger } from 'ng-primitives/menu';
import { NgpSelectPortal } from 'ng-primitives/select';
import { NgpTooltipTrigger } from 'ng-primitives/tooltip';
import {
  UiButton,
  UiBadge,
  UiDialog,
  UiDialogDescription,
  UiDialogOverlay,
  UiDialogTitle,
  UiHint,
  UiInput,
  UiLabel,
  UiMenuItem,
  UiMenuPanel,
  UiMenuSeparator,
  UiPanelCard,
  UiPanelText,
  UiPanelTitle,
  UiRecordRow,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  UiTabButton,
  UiTabIndicator,
  UiTabList,
  UiTabPanel,
  UiTabs,
  UiTextarea,
  UiTooltip,
  UiToggleGroup,
  UiToggleGroupItem,
  UiToggleIndicator,
} from '../../shared/ui';

type ColorToken = {
  name: string;
  value: string;
  className: string;
  textClass: string;
};

type Principle = {
  title: string;
  description: string;
};

type WorkflowStep = {
  number: string;
  title: string;
  description: string;
};

type ComponentSpec = {
  name: string;
  primitive: string;
  description: string;
};

@Component({
  selector: 'app-dashboard',
  imports: [
    NgpMenuTrigger,
    NgpDialogTrigger,
    NgpTooltipTrigger,
    NgIcon,
    NgpSelectPortal,
    UiButton,
    UiBadge,
    UiDialog,
    UiDialogDescription,
    UiDialogOverlay,
    UiDialogTitle,
    UiHint,
    UiInput,
    UiLabel,
    UiMenuItem,
    UiMenuPanel,
    UiMenuSeparator,
    UiPanelCard,
    UiPanelText,
    UiPanelTitle,
    UiRecordRow,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
    UiTabs,
    UiTabIndicator,
    UiTabList,
    UiTabButton,
    UiTabPanel,
    UiTextarea,
    UiTooltip,
    UiToggleGroup,
    UiToggleGroupItem,
    UiToggleIndicator,
  ],
  providers: [
    provideIcons({
      heroAdjustmentsHorizontal,
      heroBolt,
      heroChatBubbleLeftRight,
      heroCheck,
      heroChevronDown,
      heroCommandLine,
      heroSparkles,
    }),
  ],
  templateUrl: './dashboard.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Dashboard {
  readonly selectedPipeline = signal('Inbound enterprise');
  readonly selectedSegments = signal<string[]>(['Leads inbound', 'Clientes ativos']);
  readonly selectedMode = signal<string[]>(['operational']);
  readonly selectedAreas = signal<string[]>(['crm', 'agents']);
  readonly selectedTab = signal('tokens');

  readonly selectedSegmentsLabel = computed(() => {
    const selectedSegments = this.selectedSegments();

    if (selectedSegments.length === 0) {
      return 'Nenhum segmento';
    }

    if (selectedSegments.length === 1) {
      return selectedSegments[0];
    }

    return `${selectedSegments.length} selecionados`;
  });

  readonly colorTokens: readonly ColorToken[] = [
    { name: 'app', value: 'var(--color-app)', className: 'bg-app', textClass: 'text-ink' },
    { name: 'panel', value: 'var(--color-panel)', className: 'bg-panel', textClass: 'text-ink' },
    { name: 'soft', value: 'var(--color-soft)', className: 'bg-soft', textClass: 'text-ink' },
    { name: 'line', value: 'var(--color-line)', className: 'bg-line', textClass: 'text-ink' },
    { name: 'ink', value: 'var(--color-ink)', className: 'bg-ink', textClass: 'text-panel' },
    { name: 'muted', value: 'var(--color-muted)', className: 'bg-muted', textClass: 'text-panel' },
    { name: 'accent', value: 'var(--color-accent)', className: 'bg-accent', textClass: 'text-panel' },
    { name: 'tomato', value: 'var(--color-tomato)', className: 'bg-tomato', textClass: 'text-panel' },
  ];

  readonly principles: readonly Principle[] = [
    {
      title: 'Light-first premium',
      description: 'Neutros quentes, contraste limpo e acento emerald reservado para estado, foco e acao.',
    },
    {
      title: 'Material granular',
      description: 'Noise aparece em superficies grandes, paineis glass e rails de workflow, sempre abaixo do conteudo.',
    },
    {
      title: 'Operacional, nao generico',
      description: 'Layouts privilegiam contexto, objetos, status e proxima acao, sem estetica de chatbot wrapper.',
    },
  ];

  readonly workflowSteps: readonly WorkflowStep[] = [
    {
      number: '01',
      title: 'Capturar',
      description: 'Entradas de CRM, conversas, produtos, billing e eventos viram contexto estruturado.',
    },
    {
      number: '02',
      title: 'Orquestrar',
      description: 'Times, agentes, regras e integracoes operam no mesmo mapa de trabalho.',
    },
    {
      number: '03',
      title: 'Decidir',
      description: 'Assistencia, analytics e memoria ajudam a priorizar com precisao.',
    },
  ];

  readonly components: readonly ComponentSpec[] = [
    {
      name: 'Button',
      primitive: 'NgpButton',
      description: 'Acoes primarias, secundarias e comandos discretos com estados acessiveis.',
    },
    {
      name: 'Menu',
      primitive: 'NgpMenu',
      description: 'Overlays de comando com glass, noise sutil, offset global e fechamento no scroll.',
    },
    {
      name: 'Input',
      primitive: 'NgpInput',
      description: 'Campo base para filtros, busca e edicao inline com foco WCAG.',
    },
    {
      name: 'Tooltip',
      primitive: 'NgpTooltip',
      description: 'Ajuda contextual curta para icones, estados e termos menos familiares.',
    },
    {
      name: 'Dialog',
      primitive: 'NgpDialog',
      description: 'Fluxos focados, confirmacoes e criacao assistida com focus trap.',
    },
    {
      name: 'Select',
      primitive: 'NgpSelect',
      description: 'Escolha simples ou multipla usando portal, options acessiveis e estilo Automato.',
    },
    {
      name: 'Textarea',
      primitive: 'NgpTextarea',
      description: 'Campos longos para prompt, contexto, notas e instrucoes operacionais.',
    },
    {
      name: 'Toggle group',
      primitive: 'NgpToggleGroup',
      description: 'Controles segmentados para modo, densidade, status e filtros rapidos.',
    },
  ];

  setSelectedPipeline(value: unknown) {
    if (typeof value === 'string') {
      this.selectedPipeline.set(value);
    }
  }

  setSelectedSegments(value: unknown) {
    if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
      this.selectedSegments.set(value);
    }
  }

  setSelectedMode(value: string[]) {
    this.selectedMode.set(value);
  }

  setSelectedAreas(value: string[]) {
    this.selectedAreas.set(value);
  }

  setSelectedTab(value: string | undefined) {
    if (value) {
      this.selectedTab.set(value);
    }
  }
}
